/**
 * The authored content of the TZ+ tour: ten habitat stops, in order.
 *
 * This table IS the source of truth (layer C) — the step array in ./index.ts is a mechanical
 * expansion of it, three steps per stop. Editing the tour means editing this file; nothing here
 * comes from the API or a CMS.
 *
 * Every stop carries both a production and a `.live` destination. The dev ones cycle four Toronto
 * Zoo habitats that exist there — giraffes, tropical fish, test-habitat1, lions — so no two
 * consecutive stops land on the same habitat. They are all at `toronto-zoo` because the tour is
 * gated on `explorer:toronto-zoo`, and a dev stop at any other zoo could only be reached by an
 * admin, whose special role bypasses habitat access and therefore tests nothing.
 * They are literal URLs, not patterns: `stepHomePath` (core/backTarget.ts) indexes `when[1]` to
 * work out where Go Back must navigate, which is why prod comes first and dev second everywhere.
 *
 * On the clip URLs: `?tab=watch&id=X` carries no `card`, so the Watch tab opens on its default
 * "Popular" card. That card only renders a MediaGrid — and therefore only mounts the MediaModal the
 * tour waits on — while `DEFAULT_FORMULA_SELECTION` (components/HighlightsList/hooks.ts) is
 * non-empty. If that default is ever emptied, add `card=album` here or every watching step stalls.
 */
export interface TzPlusStop {
  /** The row number in the flow deck. Drives the step numbering in ./index.ts. */
  n: number;
  /** Paused-banner section name. Display copy — never an id; see `slug`. */
  section: string;
  /**
   * Stable id fragment for this stop's paused step. Authored rather than derived from `section`,
   * because a step id is persisted in the history stack: deriving it would mean a copy edit to a
   * section name silently orphaned an in-flight tour.
   */
  slug: string;
  speech: string;
  /** Button 1 — opens the clip. */
  clipLabel: string;
  /** Button 2 — skips the clip and moves straight to the next stop. */
  nextLabel: string;
  habitatPath: string;
  clipId: string;
  devHabitatPath: string;
  devClipId: string;
}

/**
 * The dev destinations, all at `toronto-zoo`.
 *
 * They must be, now that the tour is gated on `explorer:toronto-zoo`: the entitlement grants the
 * habitats at one zoo, so a dev stop anywhere else walks a real pass holder straight into a gate
 * they cannot pass, and the only way to see the tour through would be an admin account — which
 * bypasses habitat access entirely and so tests nothing.
 *
 * TWO OF THESE SLUGS CONTAIN EMOJI, AND THEY ARE WRITTEN PERCENT-ENCODED ON PURPOSE. A step's
 * `when` pattern is compiled to a regex and tested against `window.location.pathname`, which
 * reports non-ASCII characters encoded. Written raw, the pattern would never match, the guide
 * banner would never render, and every stop on that habitat would fall through to its paused
 * catch-all.
 */
const GIRAFFES = '/h/toronto-zoo/african-reticulated-giraffes';
const TROPICAL_FISH = '/h/toronto-zoo/%F0%9F%90%A0-tropical-fish';
const TEST_HABITAT = '/h/toronto-zoo/test-habitat1';
const LIONS = '/h/toronto-zoo/%F0%9F%A6%81-african-lions';

const GIRAFFES_CLIP = '65ae94601a6305ab195ef505';
const TROPICAL_FISH_CLIP = '6a39562b8028c7cf22371be8';
const TEST_HABITAT_CLIP = '6a3956013d8e571e1ead9121';
const LIONS_CLIP = '6a9889913545cf2a7ae65da5';

export const TZ_PLUS_STOPS: TzPlusStop[] = [
  {
    n: 1,
    section: 'Treetops',
    slug: 'treetops',
    speech:
      "Let's start up high. This is Mstari and her new calf, Imara. Watch the little one copy every move her mom makes, " +
      "then we'll go meet the big cats.",
    clipLabel: 'Show me Imara',
    nextLabel: 'Meet the Big Cats!',
    habitatPath: '/h/toronto-zoo/masai-giraffe',
    clipId: '6a60e330b905124028adc1e1',
    devHabitatPath: GIRAFFES,
    devClipId: GIRAFFES_CLIP,
  },
  {
    n: 2,
    section: 'Mountain Tops',
    slug: 'mountain-tops',
    speech:
      "Jita's cubs are still learning the ropes, climbing, pouncing, working out their own paws. Minu usually leads the " +
      'trouble. Ready for someone with a bit more edge?',
    clipLabel: 'Show me the cubs!',
    nextLabel: 'More Big Cats!',
    habitatPath: '/h/toronto-zoo/snow-leopard',
    clipId: '68162f182fc8152ce853e04e',
    devHabitatPath: TROPICAL_FISH,
    devClipId: TROPICAL_FISH_CLIP,
  },
  {
    n: 3,
    section: 'Jungle',
    slug: 'jungle',
    speech:
      'This is Mazy, a real curious big cat. Sunning herself, sizing everything up. Watch her a while, then we’ll go ' +
      'find the wolf pack.',
    clipLabel: 'Mazy in the water!',
    nextLabel: 'Meet the Pack',
    habitatPath: '/h/toronto-zoo/amur-tigers',
    clipId: '6a5e3b978d2e47a0e1ac15fa',
    devHabitatPath: TEST_HABITAT,
    devClipId: TEST_HABITAT_CLIP,
  },
  {
    n: 4,
    section: 'Tundra',
    slug: 'tundra',
    speech:
      'The Arctic Brothers move like they share one mind. See how they read each other without a sound. Family is the ' +
      "theme from here, so let's meet another one.",
    clipLabel: 'Pack hangout',
    nextLabel: "Let's Go",
    habitatPath: '/h/toronto-zoo/arctic-wolves',
    clipId: '6a1ca5628bb4aaa0cb9fe17a',
    devHabitatPath: LIONS,
    devClipId: LIONS_CLIP,
  },
  {
    n: 5,
    section: 'Lowland Jungle',
    slug: 'lowland-jungle',
    speech:
      'Zwalani and family are all about the small moments, playful young ones and gentle bonds. When you’re ready, ' +
      "let's change the pace completely.",
    clipLabel: 'See the brothers chase!',
    nextLabel: 'Waddle on',
    habitatPath: '/h/toronto-zoo/western-lowland-gorillas',
    clipId: '6855cae9b4e86589587f3204',
    devHabitatPath: GIRAFFES,
    devClipId: GIRAFFES_CLIP,
  },
  {
    n: 6,
    section: 'Ocean Coast',
    slug: 'ocean-coast',
    speech:
      'This waddling crowd is busy, and endlessly social. Watch them dive, and gobble fish! Stay as long as you like, ' +
      "then we'll dip below the surface.",
    clipLabel: 'See a fish feeding',
    nextLabel: 'Into the water',
    habitatPath: '/h/toronto-zoo/african-penguins',
    clipId: '6a5cfe4b8d2e47a0e1aa82f2',
    devHabitatPath: TROPICAL_FISH,
    devClipId: TROPICAL_FISH_CLIP,
  },
  {
    n: 7,
    section: 'Coral Reef',
    slug: 'coral-reef',
    speech:
      "Down here, colour is a language. Follow the brightest fish and you'll usually find who runs the reef. Next up is " +
      'one of my favourite little mobs.',
    clipLabel: 'See the full tank!',
    nextLabel: 'Meet the Madagascar Mob',
    habitatPath: '/h/toronto-zoo/african-cichlids',
    clipId: '6a7b317a97d1a7181ae2ddd0',
    devHabitatPath: TEST_HABITAT,
    devClipId: TEST_HABITAT_CLIP,
  },
  {
    n: 8,
    section: 'Madagascar',
    slug: 'madagascar',
    speech:
      'Three very different neighbours, one sunny patch. The lemurs pile together, the cranes strut, and the tortoise ' +
      'takes her sweet time. When you’re ready, let’s peek behind the scenes!',
    clipLabel: 'Show me a lemur pile!',
    nextLabel: 'Go behind the scenes',
    habitatPath: '/h/toronto-zoo/ring-tailed-lemurs',
    clipId: '69cc07c8c010e0121aed10cc',
    devHabitatPath: LIONS,
    devClipId: LIONS_CLIP,
  },
  {
    n: 9,
    section: 'Vet Centre',
    slug: 'vet-centre',
    speech:
      'Here’s the Veterinary Care Centre. From a cheetah at the dentist to a python check-up, you can see it all! ' +
      'Check the events schedule for the next stream, or watch the highlights',
    clipLabel: 'Cheetah Highlight',
    nextLabel: 'Meet Wali',
    habitatPath: '/h/toronto-zoo/wildlife-health-centre',
    clipId: '67dc6cea51db375eaa869603',
    devHabitatPath: GIRAFFES,
    devClipId: GIRAFFES_CLIP,
  },
  {
    n: 10,
    section: "Wali's World",
    slug: 'walis-world',
    speech:
      'The last stop is Wali’s world and the Sumatran Orangutans. The community chat here often has people at the ' +
      'zoo itself, so you truly get coverage online and offline!',
    clipLabel: 'Show me a Wali moment!',
    nextLabel: "I'm good to go",
    habitatPath: '/h/toronto-zoo/sumatran-orangutans',
    clipId: '69cd520bc010e0121a04315c',
    devHabitatPath: TROPICAL_FISH,
    devClipId: TROPICAL_FISH_CLIP,
  },
];

/** Copy that is not per-stop. */
export const TZ_PLUS_COPY = {
  // Not rendered: both welcome heroes are a single image with these words baked into the artwork.
  // They survive as the hero's alt text, which is the only place a screen reader can hear them, so
  // they are transcribed from the images themselves rather than taken from the flow deck — the deck
  // says "Welcome to Toronto Zoo Plus!" / "We're so happy you're here", and the artwork does not.
  // The mobile hero carries the title alone, which is why the two are separate strings.
  welcomeTitle: 'Welcome to Toronto Zoo Plus',
  welcomeSubtitle: "We're excited to show you around",
  welcomeSpeech:
    'Toronto Zoo Plus is hosted on zoolife, where you have digital access to all your Toronto Zoo favourites 24/7. ' +
    'Let’s take a tour of what makes their digital habitats so special!',
  welcomeButton: "Let's Meet the Animals",
  /** Shown after a clip modal closes, before moving on. Authored per stop, worded once. */
  clipClose: "Take your time to meet any of the animals! When you’re ready, let's go to the next habitat.",
  clipCloseButton: 'On to the next!',
  farewell:
    'Hang out as long as you like in any habitat. You can find them from the home page, or click follow to add them to ' +
    'My Animals. Thanks for wandering with me!',
  farewellButton: 'Bye!',
  goBack: 'Go Back a Step',
  takeABreak: 'Explore On My Own',
  skipAhead: 'Skip Ahead to Next Habitat',
  resume: 'Continue with Guide',
  finish: 'Finish the Tour',
} as const;

/** The paused step for a stop, e.g. the "Wali's World" stop -> `paused-walis-world`. */
export const pausedStepId = (stop: TzPlusStop): string => `paused-${stop.slug}`;
